import { MutationOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { useLocale, useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import {
  CartAndLineItemGroups,
  CommerceToolsCart,
  LineItem,
  LineItemGroup,
  ShippingMethodsResponse,
} from '@/types/commercetools';
import {
  activeCouponsAtom,
  addActiveCouponAtom,
  clearLeftoverActiveCouponsAtom,
  isPromoExpiredAtom,
  promoConfigAtom,
} from '@/atoms/promoAtom';
import { basketAtom } from '@/store/atoms';
import { getCartCoupons, getCouponCandidates, mergeCouponCodes } from '@/utility/activeCoupons';
import { canApplyPromo } from '@/utility/canApplyPromo';
import { createNewCart, getCartIdFromStorage, migrateAndFetchCart } from '@/utility/cartHelpers';
import { fetchShippingMethods } from '@/utility/fetchShippingMethods';
import { getActiveCoupons, isValidCouponCode } from '@/utility/promos';
import { post, put, remove } from '@/utility/requestHelpers/clientRequestHelpers';
import { showMiniBasketErrorToast } from '@/utility/toasts/miniBasketErrorToast';
import {
  trackCartAction,
  trackCartAddProduct,
  trackCartDeleteGroup,
} from '@/utility/tracking/trackCartAction';
import { CartFeatureDetail } from '@/utility/tracking/types';
import { useCouponValidation } from './useCouponValidation';
import { useCurrency } from './useCurrency';
import { useIsMultiCouponEnabled } from './useIsMultiCouponEnabled';

export interface CartItem {
  productTypeId: number;
  productId?: number;
  shopId: number;
  sizeId: string;
  appearanceId: string;
  quantity: number;
  sellableId?: string;
  articleId?: string;
  articleName?: string;
  price: number;
  channel?: string;
  editLink: string;
  /**
   * @deprecated use the trackingData object instead.
   */
  compositions?: CreateOmatDesign[];
  trackingData?: object;
}

export function useCart() {
  const locale = useLocale();
  return useQuery<CartAndLineItemGroups | null>({
    queryKey: ['cart', locale],
    queryFn: () => migrateAndFetchCart(locale),
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnReconnect: true,
    refetchOnWindowFocus: 'always',
    retry: 3,
    placeholderData: (previousData) => previousData,
  });
}

export function useShippingMethods(cartId: string | undefined, countryCode: string | undefined) {
  return useQuery<ShippingMethodsResponse | null>({
    queryKey: ['shippingMethods', cartId, countryCode],
    queryFn: () => fetchShippingMethods(cartId!, countryCode!),
    enabled: !!cartId && !!countryCode,
    staleTime: 60 * 60 * 1000, // 60 minutes
  });
}

function useCartMutation<Params>({
  mutationFn,
  onSettled,
  onError,
  suppressErrorToast = false,
  ...options
}: Omit<MutationOptions<unknown, Error, Params>, 'mutationKey'> & {
  suppressErrorToast?: boolean;
}) {
  const locale = useLocale();
  const t = useTranslations();
  const queryClient = useQueryClient();

  const onSettledFallback = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['cart', locale] });
  }, [queryClient, locale]);

  const onErrorFallback = useCallback(
    (error: Error, variables: Params, context: unknown, mutation: unknown) => {
      if (!suppressErrorToast) {
        showMiniBasketErrorToast(t('miniBasket.errorToast'));
      }
      onError?.(error, variables, context, mutation as never);
    },
    [onError, t, suppressErrorToast]
  );

  return useMutation<unknown, Error, Params>({
    ...options,
    mutationKey: ['cart-action'],
    mutationFn,
    onError: onErrorFallback,
    onSettled: onSettled ?? onSettledFallback,
  });
}

type MutateParams = {
  lineItem: LineItem;
  payload: { quantity: number; previousQuantity: number };
};

export function useUpdateBasket(featureDetail: CartFeatureDetail) {
  const { data: currency } = useCurrency();
  const locale = useLocale();
  const setOpenBasket = useSetAtom(basketAtom);
  const queryClient = useQueryClient();

  return useCartMutation<{
    cartItems?: CartItem[];
    removeIds?: string[];
    showBasket?: boolean;
  }>({
    mutationFn: async ({ cartItems, removeIds }) => {
      if (removeIds?.length) {
        const cartId = getCartIdFromStorage();
        if (!cartId) {
          throw new Error('Cart ID not found in local storage.');
        }

        const cartData = await queryClient.ensureQueryData<CartAndLineItemGroups | null>({
          queryKey: ['cart', locale],
          queryFn: () => migrateAndFetchCart(locale),
        });
        if (!cartData) {
          throw new Error('Cart data not available.');
        }

        const lineItemIds = cartData.cart.lineItems
          .filter((lineItem) => removeIds.includes(String(lineItem.custom.fields.sprdProductId)))
          .map((lineItem) => lineItem.id);

        if (lineItemIds.length) {
          await post<CommerceToolsCart>(`/api/v1/carts/${cartId}/items/delete`, { lineItemIds });
        }
      }

      if (cartItems?.length) {
        const cartId = getCartIdFromStorage();
        if (cartId) {
          return await post<CommerceToolsCart>(`/api/v1/carts/${cartId}/items`, { cartItems });
        }
        return await createNewCart(locale, currency, cartItems);
      }
    },
    onSuccess: (_, { cartItems, showBasket }) => {
      if (cartItems?.length) trackCartAddProduct(featureDetail, cartItems);
      if (showBasket) setOpenBasket(true);
    },
  });
}

export function useAddItem(featureDetail: CartFeatureDetail) {
  const { data: currency } = useCurrency();
  const locale = useLocale();

  return useCartMutation<{ cartItems: CartItem[] }>({
    mutationFn: async ({ cartItems }) => {
      const cartId = getCartIdFromStorage();
      if (cartId) {
        return await post<CommerceToolsCart>(`/api/v1/carts/${cartId}/items`, { cartItems });
      }

      return await createNewCart(locale, currency, cartItems);
    },
    onSuccess: (_, { cartItems }) => trackCartAddProduct(featureDetail, cartItems),
  });
}

export function useUpdateQuantity(lineItemGroup: LineItemGroup) {
  return useCartMutation<MutateParams>({
    mutationFn: async ({ lineItem, payload }) => {
      const cartId = getCartIdFromStorage();

      if (!cartId) {
        throw new Error('Cart ID not found in local storage.');
      }

      return await put<CommerceToolsCart>(`/api/v1/carts/${cartId}/items/${lineItem.id}`, {
        quantity: payload.quantity,
      });
    },
    onSuccess: (_, { lineItem, payload }) => {
      const quantityDiff = payload.previousQuantity - payload.quantity;
      const trackingAction = quantityDiff > 0 ? 'cart-removal' : 'cart-addition';
      trackCartAction(trackingAction, lineItemGroup, lineItem, Math.abs(quantityDiff));
    },
  });
}

export function useRemoveItem(lineItemGroup: LineItemGroup) {
  return useCartMutation<{ lineItem: LineItem }>({
    mutationFn: async ({ lineItem }) => {
      const cartId = getCartIdFromStorage();

      if (!cartId) {
        throw new Error('Cart ID not found in local storage.');
      }

      return await remove<CommerceToolsCart>(`/api/v1/carts/${cartId}/items/${lineItem.id}`);
    },
    onSuccess: (_, { lineItem }) => {
      trackCartAction('cart-removal', lineItemGroup, lineItem);
    },
  });
}

export function useRemoveItemGroup() {
  return useCartMutation<{ lineItemGroup: LineItemGroup }>({
    mutationFn: async ({ lineItemGroup }) => {
      const cartId = getCartIdFromStorage();

      if (!cartId) {
        throw new Error('Cart ID not found in local storage.');
      }

      return await post<CommerceToolsCart>(`/api/v1/carts/${cartId}/items/delete`, {
        lineItemIds: lineItemGroup.lineItems.map((lineItem) => lineItem.id),
      });
    },
    onSuccess: (_, { lineItemGroup }) => trackCartDeleteGroup(lineItemGroup),
  });
}

export function useAddVoucher({ suppressErrorToast = false, keepCartCoupons = false } = {}) {
  const locale = useLocale();
  const queryClient = useQueryClient();

  return useCartMutation<{ coupons: string[] }>({
    mutationFn: async ({ coupons }) => {
      const cartId = getCartIdFromStorage();

      if (!cartId) {
        throw new Error('Cart ID not found in local storage');
      }

      // The endpoint replaces the cart's whole coupon list. `keepCartCoupons` sends the coupons
      // already on the cart along; the next coupon validation then decides which of them stay.
      const cart = queryClient.getQueryData<CartAndLineItemGroups | null>(['cart', locale]);
      const payload = {
        coupons: keepCartCoupons ? mergeCouponCodes(getCartCoupons(cart?.cart), coupons) : coupons,
      };
      return await post<CommerceToolsCart>(`/api/v1/carts/${cartId}/coupons`, payload);
    },
    retry: 3,
    suppressErrorToast,
  });
}

export function useActivateVoucher() {
  const currentPromo = useAtomValue(promoConfigAtom);
  const addActiveCoupon = useSetAtom(addActiveCouponAtom);
  const isMultiCouponEnabled = useIsMultiCouponEnabled();

  // stores the voucher in a cookie, it will be applied with useAutoApplyActiveCoupons if possible
  const applyVoucher = useCallback(() => {
    if (!currentPromo) return;

    const code = currentPromo.content['promo-tool'].campaignDetails.code;
    addActiveCoupon({
      code,
      source: 'promo',
      expires: currentPromo.content._meta.lifecycle
        ? new Date(currentPromo.content._meta.lifecycle?.expiryTime)
        : null,
      replace: !isMultiCouponEnabled,
    });
  }, [currentPromo, addActiveCoupon, isMultiCouponEnabled]);

  return { applyVoucher };
}

export function useAutoActivateVoucherFromUrl() {
  const addActiveCoupon = useSetAtom(addActiveCouponAtom);
  const isMultiCouponEnabled = useIsMultiCouponEnabled();
  const searchParams = useSearchParams();

  // Any `?couponCode=` in the URL is activated for the session (session cookie): next to any
  // other active coupon in the multi-coupon variant, replacing it in the control.
  useEffect(() => {
    const couponFromUrl = searchParams.get('couponCode');
    if (!couponFromUrl || !isValidCouponCode(couponFromUrl)) return;

    addActiveCoupon({
      code: couponFromUrl,
      source: 'url',
      expires: null,
      replace: !isMultiCouponEnabled,
    });
  }, [searchParams, addActiveCoupon, isMultiCouponEnabled]);
}

/**
 * Activates the current CMS campaign's coupon on page load when the campaign has
 * `autoApplyCoupon` flag enabled, reusing the `?couponCode=` path.
 */
export function useAutoActivateCampaignCoupon() {
  const promoConfig = useAtomValue(promoConfigAtom);
  const isExpired = useAtomValue(isPromoExpiredAtom);
  const addActiveCoupon = useSetAtom(addActiveCouponAtom);
  const isMultiCouponEnabled = useIsMultiCouponEnabled();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (isExpired) return;

    const campaignDetails = promoConfig?.content['promo-tool'].campaignDetails;
    if (!campaignDetails?.autoApplyCoupon || !campaignDetails.code) return;
    // Read the cookie, not the atom: the atom is only hydrated after mount. An already active
    // code is left alone so a persistent popup activation is not turned into a session cookie.
    const activeCoupons = getActiveCoupons();
    if (activeCoupons.some((c) => c.code === campaignDetails.code)) return;
    // Single-coupon control: a coupon from the URL (now or earlier this session) takes precedence.
    const hasUrlCoupon =
      searchParams.get('couponCode') || activeCoupons.some((c) => c.source === 'url');
    if (!isMultiCouponEnabled && hasUrlCoupon) return;

    addActiveCoupon({
      code: campaignDetails.code,
      source: 'url',
      expires: null,
      replace: !isMultiCouponEnabled,
    });
  }, [isExpired, promoConfig, addActiveCoupon, isMultiCouponEnabled, searchParams]);
}

const isSameCouponSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((code) => b.includes(code));

/**
 * Validates the active coupons together with the coupons already on the cart (pricing-service),
 * and replaces the cart's coupons with the ones the validation applied. Promo coupons from the
 * cookie are only included when `includePromoCoupons` is set.
 */
export const useApplyActiveCoupons = ({
  includePromoCoupons,
}: {
  includePromoCoupons: boolean;
}) => {
  const { mutateAsync } = useAddVoucher({ suppressErrorToast: true });
  const { data: cartAndLineItemGroups } = useCart();
  const activeCoupons = useAtomValue(activeCouponsAtom);
  const candidateCoupons = getCouponCandidates(
    cartAndLineItemGroups,
    activeCoupons,
    includePromoCoupons
  );
  const { data: appliedCoupons } = useCouponValidation(cartAndLineItemGroups, candidateCoupons);
  // `coupons:cartVersion` combos already posted. Should the cart reject a validated coupon, this
  // keeps us from re-posting it forever; a cart change bumps the version and allows a new attempt.
  const attempted = useRef<Set<string>>(new Set());

  return useCallback(async () => {
    // Nothing to do while validating, after a failed validation (reported to Raygun by the query
    // cache) or when no coupon applies: the cart drops coupons that no longer apply by itself.
    if (!cartAndLineItemGroups || !appliedCoupons?.length) return;

    const currentCoupons = getCartCoupons(cartAndLineItemGroups.cart);
    if (isSameCouponSet(appliedCoupons, currentCoupons)) return;

    const coupons = [...appliedCoupons].sort().join(',');
    const markAttempted = (cartVersion: number) =>
      attempted.current.add(`${coupons}:${cartVersion}`);
    if (attempted.current.has(`${coupons}:${cartAndLineItemGroups.cart.version}`)) return;

    markAttempted(cartAndLineItemGroups.cart.version);
    try {
      const updatedCart = (await mutateAsync({ coupons: appliedCoupons })) as
        CommerceToolsCart | undefined;
      // Posting bumps the cart version even when a coupon was rejected: don't retry against it.
      if (updatedCart) markAttempted(updatedCart.version);
    } catch {
      // The request failed: don't retry against this unchanged cart, and don't surface an error
      // toast for a background action (suppressed via useAddVoucher).
    }
  }, [appliedCoupons, cartAndLineItemGroups, mutateAsync]);
};

export const useAutoApplyActiveCoupons = (options: { includePromoCoupons: boolean }) => {
  const applyActiveCoupons = useApplyActiveCoupons(options);

  useEffect(() => {
    void applyActiveCoupons();
  }, [applyActiveCoupons]);
};

/**
 * Single-coupon control of the multi-coupon A/B test, as before the test: posts the active coupon
 * in place of the cart's coupons. A URL coupon is always posted; a promo coupon
 * only when `includePromoCoupons` is set and its campaign conditions are met (`canApplyPromo`).
 */
export const useApplySingleCoupon = ({ includePromoCoupons }: { includePromoCoupons: boolean }) => {
  const { mutateAsync: postPromoCoupon } = useAddVoucher();
  const { mutateAsync: postUrlCoupon } = useAddVoucher({ suppressErrorToast: true });
  const { data: cartAndLineItemGroups } = useCart();
  const promoConfig = useAtomValue(promoConfigAtom);
  const activeCoupons = useAtomValue(activeCouponsAtom);
  // More than one coupon is a leftover from the multi-coupon variant, about to be cleared by
  // useClearLeftoverActiveCoupons: apply none of them.
  const coupon = activeCoupons.length === 1 ? activeCoupons[0] : undefined;
  // `code:cartVersion` combos already posted, so a URL coupon the backend ignores is not re-posted
  // against an unchanged cart; a cart change bumps the version and allows a fresh attempt.
  const attempted = useRef<Set<string>>(new Set());

  return useCallback(async () => {
    if (!coupon || !cartAndLineItemGroups) return;

    if (coupon.source === 'promo') {
      if (!includePromoCoupons || !canApplyPromo(cartAndLineItemGroups, promoConfig, coupon)) {
        return;
      }
      // a failure shows the mini basket error toast (useAddVoucher) and goes to Raygun
      await postPromoCoupon({ coupons: [coupon.code] }).catch(() => {});
      return;
    }

    const { version } = cartAndLineItemGroups.cart;
    const attemptKey = (cartVersion: number) => `${coupon.code}:${cartVersion}`;
    const cartCoupons = getCartCoupons(cartAndLineItemGroups.cart);
    if (cartCoupons.includes(coupon.code) || attempted.current.has(attemptKey(version))) return;

    attempted.current.add(attemptKey(version));
    try {
      const updatedCart = (await postUrlCoupon({ coupons: [coupon.code] })) as
        CommerceToolsCart | undefined;
      // Posting bumps the cart version even when the coupon was ignored: don't retry against it.
      if (updatedCart) attempted.current.add(attemptKey(updatedCart.version));
    } catch {
      // Rejected (e.g. conditions not met): don't retry against this unchanged cart, and don't
      // surface an error toast (suppressed via useAddVoucher).
    }
  }, [
    cartAndLineItemGroups,
    coupon,
    includePromoCoupons,
    postPromoCoupon,
    postUrlCoupon,
    promoConfig,
  ]);
};

export const useAutoApplySingleCoupon = (options: { includePromoCoupons: boolean }) => {
  const applySingleCoupon = useApplySingleCoupon(options);

  useEffect(() => {
    void applySingleCoupon();
  }, [applySingleCoupon]);
};

// Single-coupon control: resets coupons left over from the multi-coupon variant once. The cart is
// left alone: it keeps whatever coupons were applied to it.
export const useClearLeftoverActiveCoupons = () => {
  const clearLeftoverActiveCoupons = useSetAtom(clearLeftoverActiveCouponsAtom);

  useEffect(() => {
    clearLeftoverActiveCoupons();
  }, [clearLeftoverActiveCoupons]);
};
